import React, { useEffect, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';
import CloseIcon from '@material-ui/icons/Close';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import Box from '@material-ui/core/Box';
import { listImages, uploadImageToCloudflare } from '../../../../utils/apiWrapper';

const NO_IMAGE_PLACEHOLDER = {
  id: 'no-image',
  filename: 'No Image'
};

const IMAGES_PER_PAGE = 6;

const ImagePickerModal = ({ isOpen, onClose, onChange, listCode }) => {
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [filtered, setFiltered] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchImages();
    }
    currentPage !== 1 && setCurrentPage(1);
    // eslint-disable-next-line
  }, [isOpen, filtered]);

  const fetchImages = async () => {
    setLoading(true);
    try {
      const result = await listImages();
      let data = result;
      let filteredImages = data;
      if (filtered) {
        filteredImages = data.filter((img) => img.meta && img.meta.listCode === listCode);
      }
      setImages([NO_IMAGE_PLACEHOLDER, ...filteredImages]);
    } catch (error) {
      console.error('Failed to fetch images', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
  };

  const handleSelect = () => {
    const syntheticEvent = { target: { value: "" } };
    if (selectedImage?.id === 'no-image') {
      syntheticEvent.target.value = '';
      onChange(syntheticEvent, 'image', 'url');
      onChange(syntheticEvent, 'image', 'filename');
    } else if (selectedImage) {
      syntheticEvent.target.value = selectedImage.variants[0];
      onChange(syntheticEvent, 'image', 'url');
      syntheticEvent.target.value = selectedImage.filename;
      onChange(syntheticEvent, 'image', 'filename');
    }
    onClose();
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await uploadImageToCloudflare({
        imageBase64: base64.split(',')[1],
        fileName: file.name,
        metadata: { listCode }
      });
      setFile(null);
      await fetchImages();
    } catch (err) {
      console.error('Upload failed', err);
    } finally {
      setUploading(false);
    }
  };

  const totalPages = Math.ceil(images.length / IMAGES_PER_PAGE);
  const currentImages = images.slice((currentPage - 1) * IMAGES_PER_PAGE, currentPage * IMAGES_PER_PAGE);

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Veldu eða settu nýja mynd
        <IconButton aria-label="close" onClick={onClose} style={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box mb={2} display="flex" alignItems="center" gap={2} p={2} border={1} borderColor="#E2E8F0" borderRadius={8} bgcolor="#f7fafc" boxShadow={1} minHeight={80}>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            disabled={uploading}
            style={{ flex: 1 }}
          />
          <Button
            onClick={handleUpload}
            disabled={!file || uploading}
            color="primary"
            variant="contained"
            style={{ minWidth: 100 }}
          >
            {uploading ? <CircularProgress size={24} /> : 'Upload'}
          </Button>
        </Box>
        <Box display="flex" justifyContent="center" mb={2}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setFiltered(!filtered)}
          >
            {filtered ? 'Sjá allar myndir' : 'Sjá bara myndir fyrir þessa sögu'}
          </Button>
        </Box>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <>
            <Grid container spacing={2}>
              {currentImages.map((image) => (
                <Grid item xs={4} key={image.id}>
                  <Box
                    onClick={() => handleImageClick(image)}
                    style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: selectedImage?.id === image.id ? '2px solid blue' : '1px solid #eee',
                      padding: 10,
                      minHeight: 100,
                      borderRadius: 8,
                      background: '#fff',
                    }}
                  >
                    {image.id === 'no-image' ? (
                      <span>Engin mynd</span>
                    ) : (
                      <img src={image.variants[0]} alt={image.filename} style={{ maxWidth: '100%', maxHeight: 80 }} />
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Box display="flex" justifyContent="space-between" mt={2} position="relative">
              <IconButton
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                aria-label="Previous Page"
                style={{ position: 'absolute', left: 0, top: -30 }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                aria-label="Next Page"
                style={{ position: 'absolute', right: 0, top: -30 }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="primary" variant="contained" onClick={handleSelect} disabled={!selectedImage}>
          Select
        </Button>
        <Button onClick={onClose} variant="outlined">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImagePickerModal;

